import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { orgBasePath, ROUTES } from '@/lib/routes';

/**
 * Redirects a legacy root-level org-scoped URL (e.g. `/agents/abc`) to the
 * org-scoped equivalent (`/orgs/:activeSlug/agents/abc`) using the current
 * active membership. Used to keep bookmarks working after the org-slug
 * URL migration.
 *
 * If the user genuinely has no membership, fall back to `/profile`. The
 * loading state delegates to the parent `AuthGate`, so by the time this
 * component renders we already have authentication state.
 */
export function LegacyRedirect() {
  const { pathname, search, hash } = useLocation();
  const { activeMembership, memberships } = useAuth();
  const slug = activeMembership?.organization.slug ?? memberships[0]?.organization.slug ?? null;

  if (!slug) {
    return <Navigate to={ROUTES.profile} replace />;
  }

  // Special cases for renamed pages that still appear in older docs/bookmarks,
  // plus server-issued OAuth callback targets (`/integrations/slack?...`,
  // `/integrations/linear?...`, `/integrations/jira?...`,
  // `/integrations/google-workspace?...`) that need to land on the slug-scoped
  // Organization page in one redirect so the query string survives — chaining
  // a second `<Navigate to="…">` would silently drop `search`.
  const mappedPath =
    pathname === '/admin/agent-models'
      ? '/configure/build/agents/models'
      : pathname === '/personas'
        ? '/configure/build/agents/personas'
        : pathname === '/integrations/slack'
          ? '/configure/connect/plugins/slack'
          : pathname === '/integrations/linear'
            ? '/configure/connect/plugins/linear'
            : pathname === '/integrations/jira'
              ? '/configure/connect/plugins/jira'
              : pathname === '/integrations/google-workspace'
                ? '/configure/connect/plugins/google-workspace'
                : pathname;
  const target = `${orgBasePath(slug)}${mappedPath}${search}${hash}`;
  return <Navigate to={target} replace />;
}
